import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, List, ListItem, ListItemText, Checkbox, CircularProgress,
  InputAdornment, IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import baseService from '../services/baseService';

const SelectableDialog = ({
  open,
  onClose,
  onSave,
  title = 'Selecione os Itens',
  entity,
  column = 'name',
  columnLabel = 'name',
  valueKey = 'id',
  selectedItems = [],
  isMulti = true,
  pageSize = 25,
}) => {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);

  const listRef = useRef(null);
  const selectedRef = useRef([]);
  const fetchingRef = useRef(false); // evita chamadas duplicadas
  const itemsRef = useRef([]); // evita loop no fetchData
  const searchInputRef = useRef(null); // mantém foco
  const scrollTimeoutRef = useRef(null); // para debounce do scroll

  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    if (open) {
      const normalized = selectedItems.map(item => ({
        [valueKey]: item.value,
        [columnLabel]: item.label,
      }));
      setSelected(normalized);
      setSearch('');
      setItems([]);
      setFilteredItems([]);
      setPage(1);
      setTotalPages(1);
      fetchData('', 1, false);
    }
  }, [open, selectedItems, valueKey, columnLabel]);

  const mergeItemsWithSelected = (apiItems, selectedItems) => {
    const map = new Map();

    apiItems.forEach(item => {
      map.set(item[valueKey], item);
    });

    selectedItems.forEach(item => {
      const isComplete = item[columnLabel] !== undefined && item[columnLabel] !== '';
      if (!map.has(item[valueKey]) && isComplete) {
        map.set(item[valueKey], item);
      }
    });

    return Array.from(map.values());
  };

  const filterItems = (itemsList, term) => {
    if (!term) return itemsList;
    return itemsList.filter(item =>
      String(item[columnLabel]).toLowerCase().includes(term.toLowerCase())
    );
  };

  const fetchData = useCallback(async (searchTerm, pageNum, append = false) => {
    if (fetchingRef.current) return;
    if (pageNum > totalPages) {
      fetchingRef.current = false;
      setLoading(false);
      return; // não busca se página maior que totalPages
    }
    fetchingRef.current = true;
    setLoading(true);
    try {
      const filters = {};
      if (searchTerm) {
        filters[column] = searchTerm;
      }

      const params = {
        filters,
        page: pageNum,
        perPage: pageSize,
      };

      const response = await baseService.autocomplete(entity, params);

      const data = response?.result?.data || [];
      const lastPage = response?.result?.last_page || 1;

      // Atualiza totalPages para prevenir chamadas além do limite
      setTotalPages(lastPage);

      const merged = mergeItemsWithSelected(data, selectedRef.current);

      if (append) {
        const combined = mergeItemsWithSelected([...itemsRef.current, ...merged], selectedRef.current);
        setItems(combined);
        setFilteredItems(filterItems(combined, searchTerm));
      } else {
        setItems(merged);
        setFilteredItems(filterItems(merged, searchTerm));
      }

    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      if (!append) {
        setItems([]);
        setFilteredItems([]);
      }
      setTotalPages(1);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [entity, column, pageSize, valueKey, columnLabel, totalPages]);

  const typingTimeoutRef = useRef(null);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);

    // filtra localmente
    const localFiltered = filterItems(items, val);
    setFilteredItems(localFiltered);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      if (val && localFiltered.length === 0 && !loading && !fetchingRef.current) {
        setPage(1);
        fetchData(val, 1, false);
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }
    }, 700);
  };

  const isSelected = (item) => selected.some(i => String(i[valueKey]) === String(item[valueKey]));

  const handleToggle = (item) => {
    if (isMulti) {
      if (isSelected(item)) {
        setSelected(prev => prev.filter(i => String(i[valueKey]) !== String(item[valueKey])));
      } else {
        setSelected(prev => [...prev, item]);
      }
    } else {
      setSelected([item]);
    }
  };

  const handleClear = () => setSelected([]);

  useEffect(() => {
    if (open) {
      fetchData(search, page, page > 1);
    }
  }, [open, fetchData, page]);

  const handleScroll = () => {
    if (scrollTimeoutRef.current) return; // já aguardando debounce, ignora

    scrollTimeoutRef.current = setTimeout(() => {
      if (loading || fetchingRef.current) {
        scrollTimeoutRef.current = null;
        return;
      }
      if (page >= totalPages) {
        scrollTimeoutRef.current = null;
        return;
      }
      const el = listRef.current;
      if (!el) {
        scrollTimeoutRef.current = null;
        return;
      }

      const scrollPosition = el.scrollTop + el.clientHeight;
      const threshold = el.scrollHeight - 100;

      if (scrollPosition >= threshold) {
        setPage(prev => Math.min(prev + 1, totalPages));
      }
      scrollTimeoutRef.current = null;
    }, 200); // debounce 200ms
  };

  useEffect(() => {
    const el = listRef.current;
    if (el) {
      el.addEventListener('scroll', handleScroll);
      return () => el.removeEventListener('scroll', handleScroll);
    }
  }, [loading, page, totalPages]);

  const handleSave = () => {
    onSave(selected);
    onClose();
  };

  const handleExecuteSearch = () => {
    setPage(1);
    fetchData(search, 1, false);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{title}</DialogTitle>

      <DialogContent sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <TextField
          fullWidth
          label="Buscar"
          value={search}
          inputRef={searchInputRef}
          onChange={handleSearchChange}
          margin="normal"
          disabled={loading}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleExecuteSearch();
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  <IconButton onClick={handleExecuteSearch} aria-label="Buscar" edge="end" >
                    <SearchIcon />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />

        <List
          dense
          sx={{ maxHeight: 300, overflowY: 'auto' }}
          ref={listRef}
        >
          {filteredItems.length === 0 && !loading && (
            <ListItem>
              <ListItemText primary="Nenhum item encontrado" />
            </ListItem>
          )}

          {filteredItems.map(item => (
            <ListItem
              key={item[valueKey]}
              button={'true'}
              onClick={() => handleToggle(item)}
              dense
            >
              <Checkbox
                edge="start"
                checked={isSelected(item)}
                tabIndex={-1}
                disableRipple
              />
              <ListItemText primary={item[columnLabel]} />
            </ListItem>
          ))}

          {loading && (
            <ListItem sx={{ justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </ListItem>
          )}
        </List>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClear} disabled={loading}>
          Limpar
        </Button>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SelectableDialog;
